import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import { Alert, AlertDescription } from "@pgs-core/ui/components/alert";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Label } from "@pgs-core/ui/components/label";
import { LoadingSpinner } from "@pgs-core/ui/components/loading-spinner";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { Textarea } from "@pgs-core/ui/components/textarea";
import { formatTimestamp } from "@pgs-core/portal-lib/format";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { ImageField } from "../components/image-field";
import {
  createSupportRequest,
  fetchSupportRequests,
  SUPPORT_STATUS_LABELS,
  type SupportRequest,
  type SupportRequestStatus,
} from "../lib/support";
import type { MediaItem } from "../lib/media";
import type { ClientOutletContext } from "../lib/outlet-context";

const MAX_DESCRIPTION_LENGTH = 2000;

function statusBadgeVariant(status: SupportRequestStatus) {
  if (status === "done") return "success";
  if (status === "in_progress") return "warning";
  return "secondary";
}

function RequestHistory({ requests }: { requests: SupportRequest[] }) {
  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
        <p className="text-sm text-muted-foreground">No update requests yet.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <div key={request.id} className="rounded-lg border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="whitespace-pre-wrap text-sm">{request.description}</p>
            <Badge variant={statusBadgeVariant(request.status)} className="shrink-0">
              {SUPPORT_STATUS_LABELS[request.status]}
            </Badge>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Requested {formatTimestamp(request.created_at)}
            {request.status === "done" && request.completed_at
              ? ` · completed ${formatTimestamp(request.completed_at)}`
              : ""}
            {request.image_key ? " · image attached" : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

export function SupportPage() {
  const { api, setBreadcrumbs } = useOutletContext<ClientOutletContext>();

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Support" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  const [description, setDescription] = useState("");
  const [image, setImage] = useState<MediaItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const query = useApiQuery(
    () => (api ? fetchSupportRequests(api) : null),
    [api]
  );
  const requests = query.data?.requests ?? [];
  const quota = query.data?.quota ?? null;
  const quotaReached = quota !== null && quota.remaining <= 0;

  const submit = async () => {
    if (!api || !description.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await createSupportRequest(api, {
        description: description.trim(),
        image_key: image?.key ?? null,
      });
      setDescription("");
      setImage(null);
      query.reload();
      setNotice({
        ok: true,
        text: "Request sent — we'll action it within 24 hours and email you when it's done.",
      });
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Support"
        description="Send us the change you'd like made to your website — we'll handle it within 24 hours."
      />

      {notice && (
        <Alert variant={notice.ok ? "default" : "destructive"} className="mb-4">
          {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{notice.text}</AlertDescription>
        </Alert>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Request an update</CardTitle>
            <CardDescription>
              Describe the change — new photos, updated prices, fresh wording — and attach an
              image if it helps.
              {quota && (
                <>
                  {" "}Your plan includes {quota.limit} updates a month
                  {quotaReached ? "." : ` — ${quota.remaining} left this month.`}
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {quotaReached && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You've used all {quota.limit} included updates this month. The form reopens on
                  the 1st of next month — if something urgent comes up before then, just email us.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="support-description">What would you like changed?</Label>
              <Textarea
                id="support-description"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                maxLength={MAX_DESCRIPTION_LENGTH}
                rows={5}
                disabled={quotaReached}
                placeholder='e.g. "Please swap the photo on the home page for the attached one and update our Saturday opening hours to 9–5."'
              />
            </div>
            <div className="space-y-1.5">
              <Label>Attach an image (optional)</Label>
              <ImageField
                api={api}
                value={image ? { key: image.key, url: image.url } : null}
                onChange={setImage}
                emptyLabel="No image attached"
                chooseLabel="Attach image"
                replaceLabel="Replace image"
              />
            </div>
            <Button onClick={submit} disabled={submitting || quotaReached || !description.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send request
            </Button>
          </CardContent>
        </Card>

        <div>
          <h2 className="mb-3 text-sm font-semibold">Your requests</h2>
          {query.error && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{query.error}</AlertDescription>
            </Alert>
          )}
          {query.data === null && !query.error ? (
            <LoadingSpinner className="py-10" />
          ) : (
            <RequestHistory requests={requests} />
          )}
        </div>
      </div>
    </div>
  );
}
